import Tooltip from "@/tooltip";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { addYears, format, isAfter, parse, subYears } from "date-fns";
import { merge, setWith } from "lodash";
import { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import { connect } from "react-redux";
import Select from "react-select";
import { Actions as EMPLOYEEACTIONS } from "../../../store/actions/employeeActions";
import { handleRawDate } from "../../common/DateComponents";
import { validateFloat, validateInteger } from "../../common/validation/Validators";
import DownloadForms from "../DownloadForms";

import { mapTaxesForDropdown } from "@/helpers/Remappers";
import cleanDeep from "clean-deep";

const sickBenefitOption = [
	{ label: "Not Applicable", value: undefined },
	{ label: "Single", value: "Single" },
	{ label: "Married", value: "Married" },
];

const customTaxRateType = [
	{ label: "%", value: "yes" },
	{ label: "€", value: "no" },
];

const directorsCalculationOptions = [
	{ label: "Annual (AN)", value: "AN" },
	{ label: "Alternative (AL)", value: "AL" },
];

const cisBusinessTypeOptions = [
	{ label: "Sole Trader", value: "sole trader" },
	{ label: "Partnership", value: "partnership" },
	{ label: "Trust", value: "trust" },
	{ label: "Company", value: "company" },
];

const TaxDetails = ({
	getTaxOptions,
	forNewEmployee,
	tax,
	taxRateOptions,
	socialSecurityOptions,
	confirmModal,
	disabled,
	setTax,
	taxSettings,
	deleteTaxModal,
	prevPage,
	confirmChange,
	nextPage,
	jurisdiction,
	studentLoanCategory,
	employeeStartDate,
	sickPaySchemesOptions,
	taxConfirmModal,
	forReviewAgreement,
	forReview,
	agreement,
	downloadEmployeeForm,
	type,
	title ,
	notification,
}) => {
	const [data, setData] = useState({});
	const updateField = (field, value) => {
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);

		if (["minimumTax"].includes(field)) {
			value = typeof value === "string" ? ["yes", "true"].includes(value) : value;
		}

		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });

		setData({ ...newData });
	};

	const taxID = useMemo(() => {
		return String(tax?.id);
	}, [tax?.id]);

	useEffect(() => {
		getTaxOptions();
	}, []);

	useEffect(() => {
		const hasTax = tax && Object.keys(cleanDeep(tax))?.length > 0;
		if (!hasTax) return;
		setData({
			minimumTax: ["true", "yes"].includes(String(tax.minimumTax)),
			taxStatus: tax.taxStatus,
			employerPe: tax.employerPe,
			employmentStatus: tax.employmentStatus,
			customTaxRate: {
				rate: tax.customTaxRate?.rate,
				percentage: tax.customTaxRate?.percentage,
			},
			sickBenefit: tax.sickBenefit,
			startDate: forReview ? tax.startDate : undefined,
			postgradLoan: tax.postgradLoan,
			studentLoan: tax.studentLoan,
			w1m1: tax.w1m1,
			sickPayScheme: tax.sickPayScheme,
			nicStatus: tax.nicStatus,
			nicType: tax.nicType,
			taxCode: tax.taxCode,
			directorsCalculation: tax.directorsCalculation,
			declaration: tax.declaration,


			// CIS fields
			businessType: tax.businessType,
			tradingName: tax.tradingName,
			utr: tax.utr,
			crn: tax.crn,
			partnershipUtr: tax.partnershipUtr,
			partnershipName: tax.partnershipName,
		});
	}, [taxID, socialSecurityOptions?.length, taxRateOptions?.length]);

	const nicSubOptions = socialSecurityOptions?.find((item) => item.value == data.nicType)?.subOptions;

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		data.id = tax?.id;
		if (data.postgradLoan) data.postgradLoan = "PGL";
		if (data.w1m1) data.w1m1 = true;

		if (!["521", "511"].includes(String(data.taxStatus))) data.customTaxRate = null;

		switch (jurisdiction) {
			case "MT":
				if (!data.taxStatus) {
					NotificationManager.error("Tax Rate is Required", "Saving Tax");
					return;
				}
				break;
			case "UK":
				if (!data.taxCode && !["cis"].includes(type)) {
					NotificationManager.error("Tax Code is Required", "Saving Tax");
					return;
				}
				if (!data.businessType && ["cis"].includes(type)) {
					NotificationManager.error("Business Type is Required", "Saving Contractor Verification");
					return;
				}
				break;
		}

		let newDate;
		let { startDate } = { ...data };

		if (forReview) {
			if (Object.prototype.toString.call(startDate) === "[object Date]")
				startDate = format(startDate, "yyyy-MM-dd");
			newDate = startDate != forReview?.originalDate;
			delete data?.startDate;
		}

		confirmModal({
			input: cleanDeep(data, { emptyStrings: false, emptyArrays: false, emptyObjects: false }),
			status: true,
			formType: "Tax",
			previousDate: tax?.startDate,
		});

		if (forReview)
			confirmChange({
				date: newDate ? startDate : forReview.originalDate,
				replace: !newDate,
				agreement: forReviewAgreement,
				notification,
			});

		if (forNewEmployee) {
			confirmChange({ date: employeeStartDate, replace: taxSettings?.length > 0, onSuccess: nextPage });
		}

		// console.log('Submited Data', cleanDeep(data, { emptyStrings: false, emptyArrays: false, emptyObjects: false }))
	};

	return (
		<form
			onSubmit={onSubmit}
			className="px-8"
		>
			{!forNewEmployee && (
				<div className="col-xs-12 form-section">
					<div className="col-xs-2 col-lg-2 section-header">
						<h1 className="block-title">{title}</h1>
					</div>
					<div className="col-lg-10 col-xs-10">
						{forReview ? (
							<div className="form-material">
								<label htmlFor="material-text">Effective Date</label>
								<DatePicker
									autoComplete="off"
									dateFormat="dd MMM yyyy"
									minDate={subYears(new Date(), 30)}
									maxDate={addYears(new Date(), 10)}
									className="form-control form-input"
									onChange={(date) => {
										updateField("startDate", date ? format(date, "yyyy-MM-dd") : undefined);
									}}
									onChangeRaw={(e) => {
										handleRawDate(e.target.value, (date) => {
											updateField("startDate", date ? format(date, "yyyy-MM-dd") : undefined);
										});
									}}
									strictParsing
									selected={data?.startDate && parse(data?.startDate, "yyyy-MM-dd", new Date())}
								/>
							</div>
						) : (
							<>
								<Select
									options={taxSettings}
									value={taxSettings?.find((t) => t.value == tax?.id)}
									onChange={(newTax) => {
										setTax(newTax.value);
									}}
								/>
								{!disabled &&
									taxSettings &&
									taxSettings.length > 1 &&
									tax.id == taxSettings[taxSettings.length - 1].value && (
										<button
											className="btn btn-danger pull-right"
											type="submit"
											style={{
												position: "absolute",
												top: 6,
												right: 50,
												fontSize: 12,
												fontWeight: "normal",
												padding: "4px 8px",
											}}
											onClick={(e) => {
												e.stopPropagation();
												e.preventDefault();
												deleteTaxModal();
											}}
										>
											Delete
										</button>
									)}
							</>
						)}
						{/* <input type="hidden" name="tax-settings[0][summary]" /> */}
					</div>
				</div>
			)}

			{taxSettings[0]?.value == tax?.id && jurisdiction == "UK"  && type !== "cis" && (
				<div className="col-xs-12 form-section">
					<div className="col-xs-3 section-header">
						<h1 className="block-title">Declaration</h1>
					</div>
					<div className="col-xs-12 form-container">
						<div
							className="col-xs-12 p-5 bg-primary text-center mb-0"
							style={{
								borderRadius: "10px 10px 0px 0px",
								boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
								fontWeight: 600,
								fontSize: "1.6rem",
								zIndex: 1,
							}}
						>
							<div className="col-xs-4">Statement A</div>
							<div className="col-xs-4">Statement B</div>
							<div className="col-xs-4">Statement C</div>
						</div>
						<div
							className="col-xs-12 p-5 bg-white"
							style={{ borderRadius: "0px 0px 10px 10px" }}
						>
							<div
								className="col-xs-12 p-0"
								style={{ fontSize: "1.4rem" }}
							>
								<div className="col-xs-4">
									<span>
										This is the employee's first job since{" "}
										{format(new Date(employeeStartDate), "do MMM")} and since{" "}
										{format(new Date(employeeStartDate), "do MMM")} the employee has not received
										payments from any of the following:
									</span>
									<ul style={{ listStyleType: "disc", paddingLeft: "15px", marginTop: 5 }}>
										<li>Jobseeker's allowance</li>
										<li>Employment and Support Allowance</li>
										<li>Incapacity Benefit</li>
									</ul>
								</div>
								<div
									className="col-xs-4"
									style={{
										borderLeft: "3px solid #f0f0f0",
										borderRight: "3px solid #f0f0f0",
									}}
								>
									<span>
										Since {format(new Date(employeeStartDate), "do MMM")} the employee has had another
										job but has not had a P45 and/or since{" "}
										{format(new Date(employeeStartDate), "do MMM")} the employee has received payments
										from any of the following:
									</span>
									<ul style={{ listStyleType: "disc", paddingLeft: "15px", marginTop: 5 }}>
										<li>Jobseeker's allowance</li>
										<li>Employment and Support Allowance</li>
										<li>Incapacity Benefit</li>
									</ul>
								</div>
								<div className="col-xs-4">
									<span>
										Employee has another job and/or is in receipt of a State, workplace or private
										pension.
									</span>
								</div>
							</div>
							<div className="col-xs-12 p-0 text-center mt-3">
								<div className="col-xs-4">
									<span>Current Personal Allowance</span>
								</div>
								<div className="col-xs-4">
									<span>Current personal allowance on a Week 1/Month 1 basis</span>
								</div>
								<div className="col-xs-4">
									<span>Tax Code BR</span>
								</div>
							</div>
							<div className="col-xs-12 p-0">
								<div
									className="col-xs-4"
									style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
								>
									<label className="css-input switch switch-sm switch-primary">
										<input
											id="form-control form-input"
											name="postgradLoan"
											className="checkbox-toggle"
											checked={data?.declaration == "A"}
											onClick={() =>
												updateField("declaration", data?.declaration == "A" ? null : "A")
											}
											type="checkbox"
											autoComplete="off"
											disabled={disabled}
										/>
										<span style={{ float: "left" }} />
									</label>
								</div>
								<div
									className="col-xs-4"
									style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
								>
									<label className="css-input switch switch-sm switch-primary">
										<input
											id="form-control form-input"
											name="postgradLoan"
											className="checkbox-toggle"
											checked={data?.declaration == "B"}
											onClick={() => {
												updateField("declaration", data?.declaration == "B" ? null : "B");
												updateField("w1m1", true);
											}}
											type="checkbox"
											autoComplete="off"
											disabled={disabled}
										/>
										<span style={{ float: "left" }} />
									</label>
								</div>
								<div
									className="col-xs-4"
									style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
								>
									<label className="css-input switch switch-sm switch-primary">
										<input
											id="form-control form-input"
											name="postgradLoan"
											className="checkbox-toggle"
											checked={data?.declaration == "C"}
											onClick={() => {
												updateField("declaration", data?.declaration == "C" ? null : "C");
												updateField("taxCode", "BR");
											}}
											type="checkbox"
											autoComplete="off"
											disabled={disabled}
										/>
										<span style={{ float: "left" }} />
									</label>
								</div>
							</div>
						</div>
						<div className="col-xs-12 p-0">
							<p className="col-lg-12 col-xs-12 help-block p-0 m-0 mt-4 ml-2">
								Jobseeker's Allowance {"(JSA)"} is an unemployment benefit which can be claimed while
								looking for work. <br />
								Employment and Support Allowance {"(ESA)"} is a benefit which can be claimed if you have
								a disability or health condition that affects how much you can work. <br />
								Incapacity Benefit is help if employee could not work because of an illness or
								disability before 31st January 2011. <br />
								State Pension is a pension paid when employee reachas State Pension age. <br />
								Workplace Pension is a pension which is arranged by the employer and is being paid to
								the employee. <br />
								Private Pension is a pension arranged by the employee and is being paid the employee.{" "}
								<br />
								Please note that no other Government or HMRC paid benefits need to be considered when
								completing this form.
							</p>
						</div>
					</div>
				</div>
			)}
			<div className="col-xs-12 form-section">
				<div className="col-xs-3 section-header">
					<h1 className="block-title">{title} Details</h1>
				</div>
				{!forReview && !forNewEmployee ? (
					<DownloadForms {...{ formType: "tax", taxSettingId: tax?.id, agreementId: agreement }} />
				) : null}
				<div className="col-xs-12 form-container pl-0">
					{jurisdiction == "MT" && (
						<>
							<div className="col-xs-4">
								<div className="form-material">
									<Select
										isDisabled={disabled}
										options={socialSecurityOptions}
										value={
											socialSecurityOptions?.find(
												(item) => item.value == data.employmentStatus,
											) || null
										}
										name="employmentStatus"
										onChange={(selected) => {
											updateField("employmentStatus", selected?.value || null);
											console.debug("selected", selected.value);
											selected?.value == 519 && updateField("sickBenefit", null);
										}}
									/>
									<label htmlFor="example2-select2">Social Security Status</label>
								</div>
							</div>
							{/* If the Employment Status requires PE No then show */}
							<div className="col-xs-4 pl-0">
								{socialSecurityOptions?.find((item) => item.value == data.employmentStatus)
									?.requiresEmployerIdentification ? (
									<div className="form-material">
										<input
											className="form-control form-input"
											name="employerPe"
											value={data?.employerPe}
											onChange={(e) => {
												updateField("employerPe", e.target.value);
											}}
											disabled={disabled}
										/>
										<label htmlFor="material-text">FT Employer PE No</label>
									</div>
								) : null}
							</div>

							<div className="col-xs-12 form-container">
								<div className="col-xs-6 pl-0">
									<label className="css-input switch switch-sm switch-success">
										<input
											id="minimum-tax"
											type="checkbox"
											name="minimumTax"
											disabled={disabled}
											checked={data?.minimumTax}
											onClick={() => {
												updateField("minimumTax", !data.minimumTax);
											}}
											onChange={() => {}}
										/>
										<span />
										<label
											htmlFor="material-text"
											style={{ marginBottom: 0 }}
										>
											Employee pays minimum (10%) National Insurance
										</label>
									</label>
									<div className="clearfix" />
								</div>
							</div>

							<div
								className="col-xs-12"
								style={{ marginBottom: "2rem" }}
							>
								<div className="col-xs-4 pl-0">
									<div className="form-material">
										<Select
											isDisabled={disabled}
											isClearable
											options={taxRateOptions}
											value={
												taxRateOptions?.find((item) => item.value == data?.taxStatus) || null
											}
											required
											name="taxStatus"
											onChange={(selected) => {
												updateField("taxStatus", selected?.value || null);
											}}
										/>
										<label>Tax Rate*</label>
									</div>
								</div>
								{["521", "511"].includes(String(data.taxStatus)) ? (
									<>
										<div className="col-xs-2 pr-0">
											<div className="form-material">
												<input
													className="form-control form-input"
													name="customTaxRate[rate]"
													required={true}
													value={data?.customTaxRate?.rate}
													onChange={(e) => {
														const functionToUse =
															data.customTaxRate?.percentage == "no"
																? validateInteger
																: validateFloat;
														updateField(e.target.name, functionToUse(e.target.value));
													}}
													style={{ textAlign: "right" }}
													disabled={disabled}
												/>
											</div>
										</div>

										<div className="col-xs-2">
											<div className="form-material">
												{data?.taxStatus == 511 ? (
													<label
														htmlFor="nicStatus"
														style={{
															width: "100%",
															display: "flex !important",
															flexDirection: "row-reverse",
														}}
													>
														<Tooltip
															content={
																"Enter the tax rate for earnings exceeding €10,000. The first €10,000 are always on 10% regardless of the custom rate."
															}
														>
															<InformationCircleIcon
																className="h-5 w-5"
																style={{ marginTop: "3px" }}
															/>
														</Tooltip>
													</label>
												) : null}
												<Select
													isDisabled={disabled}
													required={true}
													options={customTaxRateType}
													value={customTaxRateType?.find(
														(item) => item.value == data?.customTaxRate?.percentage,
													)}
													onChange={(selected) => {
														updateField("customTaxRate", {
															rate: "",
															percentage: selected?.value,
														});
													}}
												/>
											</div>
										</div>
									</>
								) : null}
							</div>

							<div className="col-xs-4">
								<div className="form-material">
									<Select
										isDisabled={disabled || data?.employmentStatus == 519}
										options={sickBenefitOption}
										name="sickBenefit"
										value={sickBenefitOption?.find((item) => item.value == data?.sickBenefit)}
										onChange={(selected) => {
											updateField("sickBenefit", selected?.value || null);
										}}
									/>
									<label>Sick Benefit Rate</label>
								</div>
							</div>
						</>
					)}

					{/*Main UK Tax*/}
					{jurisdiction == "UK" && type !== "cis" ? (
						<>
							<div className="col-xs-8">
								<div className="col-xs-4 pl-0 pr-0">
									<div className="form-material form-container">
										<Select
											isDisabled={disabled}
											options={socialSecurityOptions}
											value={socialSecurityOptions?.find((item) => item.value == data.nicType)}
											name="nicType"
											placeholder="Choose one if applicable"
											onChange={(selected) => {
												updateField("nicType", selected?.value || null);
											}}
										/>
										<label htmlFor="nicType">NIC Status </label>
									</div>
								</div>
								<div className="col-xs-8 pl-0 pr-0">
									<div className="form-material form-container">
										{data.nicStatus ? (
											<>
												<label
													htmlFor="nicStatus"
													style={{
														width: "100%",
														display: "flex !important",
														flexDirection: "row-reverse",
													}}
												>
													<Tooltip
														content={
															<div style={{ whiteSpace: "nowrap" }}>
																<p style={{ color: "white", fontWeight: 700 }}>
																	Letters Explained
																</p>
																<ul style={{ whiteSpace: "nowrap", textAlign: "left" }}>
																	{nicSubOptions
																		?.find((item) => item.value == data.nicStatus)
																		?.description?.map((t) => (
																			<li>{t}</li>
																		))}
																</ul>
															</div>
														}
													>
														<InformationCircleIcon
															className="h-5 w-5"
															style={{ marginTop: "3px" }}
														/>
													</Tooltip>
												</label>
											</>
										) : null}
										<Select
											isDisabled={disabled}
											options={nicSubOptions}
											value={nicSubOptions?.find((item) => item.value == data.nicStatus)}
											isClearable={true}
											name="nicStatus"
											placeholder="Choose one if applicable"
											onChange={(selected) => {
												updateField("nicStatus", selected?.value || null);
											}}
										/>
									</div>
								</div>
							</div>

							<div className="col-xs-4">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										name="taxCode"
										value={data?.taxCode}
										style={{ width: "100%" }}
										onChange={(e) => {
											const value = e.target.value.toUpperCase();
											updateField(e.target.name, value);
										}}
										disabled={disabled}
									/>
									<label>Tax Code</label>
								</div>
							</div>

							<div className="clearfix"></div>
							<div className="col-xs-4">
								<div className="form-material form-container">
									<Select
										isDisabled={disabled}
										isClearable
										options={studentLoanCategory}
										value={studentLoanCategory?.find((item) => item.value == data.studentLoan)}
										name="studentLoan"
										placeholder="Choose one if applicable"
										onChange={(selected) => {
											updateField("studentLoan", selected?.value || null);
										}}
									/>
									<label>Student Loan</label>
								</div>
							</div>
							<div className="col-xs-4">
								<div className="form-container">
									<label
										className="css-input switch switch-sm switch-success"
										style={{ marginTop: 17 }}
									>
										<input
											id="form-control form-input"
											name="postgradLoan"
											className="checkbox-toggle"
											checked={data.postgradLoan}
											onClick={() => updateField("postgradLoan", !data.postgradLoan)}
											type="checkbox"
											disabled={disabled}
											autoComplete="off"
										/>
										<span /> Postgraduate Loan
									</label>
								</div>
							</div>
							<div className="col-xs-4">
								<div className="form-container">
									<label
										className="css-input switch switch-sm switch-success"
										style={{ marginTop: 17 }}
									>
										<input
											name="w1m1"
											className="checkbox-toggle"
											checked={data.w1m1}
											onClick={() => updateField("w1m1", !data.w1m1)}
											onChange={() => {}}
											type="checkbox"
											disabled={disabled || data?.declaration == "B"}
											autoComplete="off"
										/>
										<span /> W1M1
									</label>
								</div>
							</div>

							<div className="clearfix"></div>

							{sickPaySchemesOptions?.length > 1 && (
								<div className="col-xs-6">
									<div className="form-material form-container">
										<Select
											isDisabled={disabled}
											options={sickPaySchemesOptions?.find(
												(item) => item.value == data.sickPayScheme,
											)}
											value={data.sickPayScheme}
											name="sickPayScheme"
											placeholder="Choose one if applicable"
											onChange={(selected) => {
												updateField("sickPayScheme", selected?.value || null);
											}}
										/>
										<label>Sick Pay Scheme</label>
									</div>
								</div>
							)}
						</>
					) : null}

					{/*Directors*/}
					{jurisdiction === "UK" && type !== "cis" && (
						<div className="col-xs-4">
							<div className="col-xs-12 pl-0 pr-0">
								<div className="form-material form-container">
									<Select
										isDisabled={disabled}
										options={directorsCalculationOptions}
										value={directorsCalculationOptions?.find(
											(item) => item.value == data.directorsCalculation,
										)}
										isClearable={true}
										name="directorsCalculation"
										placeholder="Choose one if applicable"
										onChange={(selected) => {
											updateField("directorsCalculation", selected?.value || null);
										}}
									/>
									<label htmlFor="directorsCalculation">Directors' NI Calculation Method </label>
								</div>
							</div>
						</div>
					)}


					{/*UK CIS Contractors*/}
					{jurisdiction === "UK" && type === "cis" && (
						<div>
							<div className="col-xs-6">
								<div className="form-material form-container">
									<Select
										isDisabled={disabled}
										options={cisBusinessTypeOptions}
										value={cisBusinessTypeOptions?.find((item) => item.value == data.businessType)}
										name="businessType"
										onChange={(selected) => {
											updateField("businessType", selected?.value || null);
										}}
									/>
									<label>Business Type</label>
								</div>
							</div>
							<div className="col-xs-6 mb-2">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										name="tradingName"
										value={data?.tradingName}
										style={{ width: "100%" }}
										onChange={(e) => {
											const value = e.target.value.toUpperCase();
											updateField(e.target.name, value);
										}}
									/>
									<label>Trading Name</label>
								</div>
							</div>
							<div className="col-xs-6">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										name="utr"
										value={data?.utr}
										style={{ width: "100%" }}
										onChange={(e) => {
											const value = e.target.value.toUpperCase();
											updateField(e.target.name, value);
										}}
									/>
									<label htmlFor="utr">Subcontractor UTR</label>
								</div>
							</div>
							<div className="col-xs-6">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										name="crn"
										value={data?.crn}
										style={{ width: "100%" }}
										onChange={(e) => {
											const value = e.target.value.toUpperCase();
											updateField(e.target.name, value);
										}}
									/>
									<label htmlFor="crn">Subcontractor CRN</label>
								</div>
							</div>
							{ data.businessType === "partnership" && (
								<>
									<div className="col-xs-6">
										<div className="form-material form-container">
											<input
												className="form-control form-input"
												name="partnershipUtr"
												value={data?.partnershipUtr}
												style={{ width: "100%" }}
												onChange={(e) => {
													const value = e.target.value.toUpperCase();
													updateField(e.target.name, value);
												}}
											/>
											<label htmlFor="partnershipUtr">Partnership UTR</label>
										</div>
									</div>
									<div className="col-xs-6">
										<div className="form-material form-container">
											<input
												className="form-control form-input"
												name="partnershipName"
												value={data?.partnershipName}
												style={{ width: "100%" }}
												onChange={(e) => {
													const value = e.target.value.toUpperCase();
													updateField(e.target.name, value);
												}}
											/>
											<label htmlFor="partnershipName">Partnership Name</label>
										</div>
									</div>
								</>
							)}
						</div>
					)}


				</div>
			</div>

			<div className="clearfix"></div>
			{!disabled && !forNewEmployee && (
				<button
					className="btn btn-success pull-right"
					type="submit"
				>
					Update {title} Details
				</button>
			)}
			{forNewEmployee && (
				<div>
					<button
						className="btn btn-default"
						onClick={prevPage}
					>
						Back
					</button>

					<button
						className="btn btn-success right"
						type="submit"
					>
						Save &amp; Continue
					</button>
				</div>
			)}
			<div className="clearfix" />
		</form>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { socialSecurityStatus, taxRates, studentLoans: studentLoanCategory } = { ...state.taxOptions };
	const { taxSettings, selectedTax, agreementInfo } = state.employee;
	const { forReview, forNewEmployee } = ownProps;
	const jurisdiction = state.company?.settings?.jurisdiction;

	const tax = forReview
		? forReview
		: selectedTax
		? taxSettings.find((t) => t.id == selectedTax)
		: taxSettings?.[taxSettings.length - 1];
	const taxRateOptions = taxRates?.map((o) => ({
		label: o.name,
		value: o.id,
	}));

	const socialSecurityOptions =
		jurisdiction === "MT"
			? [{ name: "Standard" }, ...(Array.isArray(socialSecurityStatus) ? socialSecurityStatus : [])].map((o) => ({
					label: o.name,
					value: o.id,
					requiresEmployerIdentification: ["true", "yes", true].includes(o.requiresIdentification),
			  }))
			: socialSecurityStatus;

	const sickPaySchemesOptions = state.company?.settings?.sickPaySchemes?.map((o) => ({
		label: o.name,
		value: o.id,
	}));
	const isLast = forReview ? true : tax?.id == taxSettings?.[taxSettings?.length - 1]?.id;
	const terminationDate = agreementInfo?.terminationDate;

	const isCis = state.employee?.agreementInfo?.type === "cis";

	return {
		agreement: state.employee?.agreementInfo?.id,
		taxConfirmModal: !!state.employee?.confirmModal,
		sickPaySchemesOptions,
		jurisdiction,
		tax,
		type: state.employee?.agreementInfo?.type,
		title: isCis ? "CIS Verification" : "Tax",
		taxSettings: mapTaxesForDropdown(taxSettings),
		studentLoanCategory,
		taxRateOptions,
		employeeStartDate: state?.employee?.agreementInfo?.engagementDate,
		socialSecurityOptions,
		disabled:
			state?.permissions?.my?.permissions?.people == "Read" ||
			(!forNewEmployee && terminationDate && isAfter(new Date(), new Date(terminationDate))) ||
			!isLast,
	};
};

const mapDispatchToProps = (dispatch) => ({
	confirmModal: (options) => dispatch(EMPLOYEEACTIONS.confirmModal(options)),
	setTax: (taxId) => dispatch(EMPLOYEEACTIONS.setTax(taxId)),
	deleteTaxModal: () => dispatch(EMPLOYEEACTIONS.deleteTaxModal(true)),
	getTaxOptions: () => dispatch(EMPLOYEEACTIONS.getTaxOptions()),
	confirmChange: (params) => dispatch(EMPLOYEEACTIONS.confirmChange(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TaxDetails);
